import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Loader2, ArrowLeft, Download, Clock3, CalendarCheck, FileText, Plus, Trash2, Users, Activity } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { api, formatApiError } from "@/lib/api";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  confirmed: "bg-blue-100 text-blue-700 border-blue-200",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-100 text-red-600 border-red-200",
};

export default function Account() {
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [family, setFamily] = useState([]);
  const [famForm, setFamForm] = useState({ name: "", relation: "spouse", age: "", gender: "" });
  const [famOpen, setFamOpen] = useState(false);
  const [famSaving, setFamSaving] = useState(false);
  const [timelineMember, setTimelineMember] = useState("all");
  const [downloading, setDownloading] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        let me;
        try {
          me = await api.get("/auth/customer/me");
        } catch {
          await api.post("/auth/refresh");
          me = await api.get("/auth/customer/me");
        }
        setCustomer(me.data);
        const [b, f] = await Promise.all([api.get("/customer/bookings"), api.get("/customer/family")]);
        setBookings(b.data);
        setFamily(f.data || []);
      } catch {
        navigate("/login");
      }
    })();
  }, [navigate]);

  const download = async (b) => {
    setDownloading(b.ref);
    try {
      const res = await api.get("/reports/download", { params: { ref: b.ref, phone: customer.phone }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Hemozy-Report-${b.ref}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading("");
    }
  };

  const addMember = async (e) => {
    e.preventDefault();
    if (!famForm.name.trim()) return toast.error("Enter the member's name");
    setFamSaving(true);
    try {
      const { data } = await api.post("/customer/family", {
        ...famForm,
        age: famForm.age ? Number(famForm.age) : undefined,
        gender: famForm.gender || undefined,
      });
      setFamily((p) => [...p, data]);
      setFamForm({ name: "", relation: "spouse", age: "", gender: "" });
      setFamOpen(false);
      toast.success(`${data.name} added to your family`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFamSaving(false);
    }
  };

  const removeMember = async (id) => {
    try {
      await api.delete(`/customer/family/${id}`);
      setFamily((p) => p.filter((m) => m.id !== id));
      toast.success("Member removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    navigate("/");
  };

  if (!customer || bookings === null) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" data-testid="account-loading">
        <Loader2 className="w-8 h-8 text-[#D92638] animate-spin" />
      </div>
    );
  }

  const memberNames = [...new Set([customer.name, ...family.map((m) => m.name), ...bookings.map((b) => b.patient.name)].filter(Boolean))];
  const timelineList = bookings
    .filter((b) => timelineMember === "all" || b.patient.name === timelineMember)
    .slice()
    .sort((a, b) => (a.slot_date || a.created_at || "").localeCompare(b.slot_date || b.created_at || ""));
  const metricPoints = timelineList
    .filter((b) => b.metrics && Object.keys(b.metrics).length > 0)
    .map((b) => ({ date: (b.slot_date || b.created_at || "").slice(5, 10), ref: b.ref, ...b.metrics }));

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="account-page">
      <header className="bg-[#0B192C] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/"><Logo /></Link>
            <span className="hidden sm:inline text-xs font-semibold tracking-widest uppercase text-slate-400 border-l border-white/15 pl-4">My Account</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors" data-testid="account-back-home">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
            <button onClick={logout} data-testid="account-logout-button"
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-white border border-white/15 rounded-full px-4 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0B192C] tracking-tight">Hello, {customer.name || "there"}</h1>
          <p className="mt-1 text-sm text-slate-500">All your Hemozy bookings and reports, linked to {customer.phone}.</p>

          {/* Family profiles */}
          <div className="mt-8 bg-white rounded-2xl border border-slate-200/90 p-6 shadow-[0_4px_20px_-4px_rgba(15,25,44,0.06)]" data-testid="family-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0D9488]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#0D9488]" />
                </div>
                <div>
                  <h2 className="font-bold text-[#0B192C]">Family profiles</h2>
                  <p className="text-xs text-slate-500">Book tests & keep reports for spouse, kids, parents - one account</p>
                </div>
              </div>
              <button onClick={() => setFamOpen(!famOpen)} data-testid="family-add-toggle"
                className="flex items-center gap-1.5 text-xs font-semibold text-white bg-[#0D9488] hover:bg-[#0B192C] px-4 py-2.5 rounded-full transition-all active:scale-95">
                <Plus className="w-3.5 h-3.5" /> Add member
              </button>
            </div>

            {family.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2.5">
                {family.map((m) => (
                  <div key={m.id} className="flex items-center gap-2.5 bg-[#F8FAFC] border border-slate-200 rounded-full pl-4 pr-2 py-2" data-testid={`family-member-${m.id}`}>
                    <span className="text-sm font-medium text-[#0B192C]">{m.name}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[#0D9488] bg-[#0D9488]/10 rounded-full px-2 py-0.5">{m.relation}{m.age ? ` · ${m.age}y` : ""}</span>
                    <button onClick={() => removeMember(m.id)} aria-label={`Remove ${m.name}`} data-testid={`family-remove-${m.id}`}
                      className="w-6 h-6 rounded-full hover:bg-red-50 flex items-center justify-center text-slate-400 hover:text-red-500 transition-colors">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {family.length === 0 && !famOpen && (
              <p className="mt-4 text-xs text-slate-400" data-testid="family-empty">No family members yet. Add your spouse, kids or parents to book for them and keep their reports here.</p>
            )}

            {famOpen && (
              <form onSubmit={addMember} className="mt-5 grid sm:grid-cols-4 gap-3 bg-[#F8FAFC] border border-slate-200 rounded-2xl p-4" data-testid="family-add-form">
                <input value={famForm.name} onChange={(e) => setFamForm({ ...famForm, name: e.target.value })} placeholder="Full name *" data-testid="family-name-input"
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
                <select value={famForm.relation} onChange={(e) => setFamForm({ ...famForm, relation: e.target.value })} data-testid="family-relation-select"
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]">
                  <option value="spouse">Spouse</option>
                  <option value="child">Child</option>
                  <option value="parent">Parent</option>
                  <option value="sibling">Sibling</option>
                  <option value="other">Other</option>
                </select>
                <input value={famForm.age} onChange={(e) => setFamForm({ ...famForm, age: e.target.value.replace(/\D/g, "").slice(0, 3) })} placeholder="Age" inputMode="numeric" data-testid="family-age-input"
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
                <button type="submit" disabled={famSaving} data-testid="family-save-button"
                  className="flex items-center justify-center gap-2 bg-[#0B192C] hover:bg-[#1E3E62] text-white text-sm font-semibold rounded-xl transition-all active:scale-95 disabled:opacity-60">
                  {famSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Save
                </button>
              </form>
            )}
          </div>

          {/* Family health timeline */}
          <div className="mt-8 bg-white rounded-2xl border border-slate-200/90 p-6 shadow-[0_4px_20px_-4px_rgba(15,25,44,0.06)]" data-testid="health-timeline">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D92638]/10 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-[#D92638]" />
                </div>
                <div>
                  <h2 className="font-bold text-[#0B192C]">Family health timeline</h2>
                  <p className="text-xs text-slate-500">Reports & health trends for each family member over time</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="timeline-member-chips">
                <button onClick={() => setTimelineMember("all")} data-testid="timeline-chip-all"
                  className={`text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-all active:scale-95 ${timelineMember === "all" ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0D9488]"}`}>
                  Everyone
                </button>
                {memberNames.map((n) => (
                  <button key={n} onClick={() => setTimelineMember(n)} data-testid={`timeline-chip-${n.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-all active:scale-95 ${timelineMember === n ? "bg-[#0D9488] text-white border-[#0D9488]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0D9488]"}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {metricPoints.length > 0 && (
              <div className="mt-6 bg-[#F8FAFC] border border-slate-200/80 rounded-2xl p-4" data-testid="metrics-chart">
                <div className="text-[10px] font-bold tracking-widest uppercase text-slate-400 mb-2">Key health trends</div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={metricPoints} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="glucose" name="Fasting sugar (mg/dL)" stroke="#D92638" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                    <Line type="monotone" dataKey="hba1c" name="HbA1c (%)" stroke="#0D9488" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                    <Line type="monotone" dataKey="cholesterol" name="Cholesterol (mg/dL)" stroke="#1E3E62" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {timelineList.length === 0 ? (
              <p className="mt-5 text-xs text-slate-400" data-testid="timeline-empty">No history yet for this selection.</p>
            ) : (
              <div className="mt-6 relative pl-6 space-y-5 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200" data-testid="timeline-entries">
                {timelineList.slice().reverse().map((b) => (
                  <div key={b.ref} className="relative" data-testid={`timeline-entry-${b.ref}`}>
                    <span className={`absolute -left-6 top-1.5 w-3.5 h-3.5 rounded-full border-2 border-white shadow ${b.status === "completed" ? "bg-emerald-500" : b.status === "confirmed" ? "bg-blue-500" : "bg-amber-400"}`} />
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="font-mono-med text-xs font-semibold text-slate-500">{b.slot_date}</span>
                      <span className="font-mono-med text-xs font-bold text-[#0B192C]">{b.ref}</span>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[b.status] || ""}`}>{b.status.toUpperCase()}</span>
                      {timelineMember === "all" && <span className="text-[10px] font-semibold text-[#0D9488] bg-[#0D9488]/10 rounded-full px-2 py-0.5">{b.patient.name}</span>}
                    </div>
                    <div className="mt-1 text-xs text-slate-600">{b.items.map((i) => i.name).join(", ")}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      {b.metrics && Object.entries(b.metrics).map(([k, v]) => (
                        <span key={k} className="font-mono-med text-[10px] font-semibold text-slate-600 bg-slate-100 rounded-full px-2.5 py-1" data-testid={`metric-badge-${b.ref}-${k}`}>
                          {k === "hba1c" ? `HbA1c ${v}%` : k === "glucose" ? `Sugar ${v} mg/dL` : `Chol ${v} mg/dL`}
                        </span>
                      ))}
                      {b.has_report && (
                        <button onClick={() => download(b)} disabled={downloading === b.ref} data-testid={`timeline-download-${b.ref}`}
                          className="flex items-center gap-1.5 text-[11px] font-semibold text-[#D92638] hover:text-[#B81D2D] transition-colors">
                          {downloading === b.ref ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Report PDF
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-8 flex items-center gap-3 mb-5">
            <CalendarCheck className="w-5 h-5 text-[#D92638]" />
            <h2 className="font-bold text-[#0B192C]">Your bookings</h2>
            <span className="text-xs font-medium text-slate-500 bg-white border border-slate-200 rounded-full px-2.5 py-1">{bookings.length}</span>
          </div>

          {bookings.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200/90 py-14 text-center" data-testid="account-no-bookings">
              <p className="text-slate-500 text-sm">No bookings yet on this number.</p>
              <Link to="/" className="mt-3 inline-block text-sm font-semibold text-[#D92638] hover:underline">Book your first test →</Link>
            </div>
          )}

          <div className="space-y-4">
            {bookings.map((b) => (
              <motion.div key={b.ref} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-[0_4px_20px_-4px_rgba(15,25,44,0.06)]" data-testid={`account-booking-${b.ref}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono-med font-bold text-[#0B192C]">{b.ref}</span>
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${STATUS_STYLES[b.status] || ""}`} data-testid={`account-status-${b.ref}`}>
                      {b.status.toUpperCase()}
                    </span>
                  </div>
                  <span className="font-mono-med font-semibold text-[#0B192C]">₹{b.total.toLocaleString("en-IN")}</span>
                </div>
                <div className="mt-3 text-sm font-medium text-[#0B192C]">For: {b.patient.name}{b.booked_for ? ` (${b.booked_for.split("(")[1]?.replace(")", "") || "family"})` : ""}</div>
                <div className="mt-1 text-sm text-slate-600">{b.items.map((i) => i.name).join(", ")}</div>
                <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5 text-[#D92638]" /> {b.slot_date} · {b.slot_time}</span>
                  {b.home_collection && <span className="font-semibold text-[#0D9488]">Home collection</span>}
                </div>
                <div className="mt-4 border-t border-slate-100 pt-4 flex items-center justify-between">
                  {b.has_report ? (
                    <button onClick={() => download(b)} disabled={downloading === b.ref} data-testid={`account-download-${b.ref}`}
                      className="flex items-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white text-xs font-semibold px-5 py-2.5 rounded-full transition-all active:scale-95 disabled:opacity-60">
                      {downloading === b.ref ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      Download report (PDF)
                    </button>
                  ) : (
                    <span className="flex items-center gap-2 text-xs text-slate-400" data-testid={`account-report-pending-${b.ref}`}>
                      <FileText className="w-3.5 h-3.5" /> Report not uploaded yet - we'll WhatsApp you when ready
                    </span>
                  )}
                  <span className="text-[10px] text-slate-400 font-mono-med">Booked {b.created_at?.slice(0, 10)}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
